import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { MapPin, Calendar, Users, Wallet, FileText, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import DestinationAutocomplete from '../components/DestinationAutocomplete';
import BookingConfirmation from '../components/BookingConfirmation';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TripPlannerPage() {
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    email: '',
    destination: '',
    travelers: '',
    travel_date: '',
    budget: '',
    requirements: '',
    inquiry_type: 'trip_planner'
  });
  const [loading, setLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [bookingDetails, setBookingDetails] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.mobile || !formData.destination) {
      toast.error('Please fill all required fields');
      return;
    }
    try {
      setLoading(true);
      
      // Create booking instead of just inquiry
      const res = await axios.post(`${API}/bookings/create`, {
        booking_type: 'package',
        customer_name: formData.name,
        customer_mobile: formData.mobile,
        customer_email: formData.email || '',
        city: formData.destination,
        travel_date: formData.travel_date,
        travelers: parseInt(formData.travelers) || 1,
        special_requests: `Budget: ${formData.budget || 'Not specified'}\n\nRequirements: ${formData.requirements || 'None'}`,
        package_name: `Custom Trip to ${formData.destination}`
      });
      
      setBookingDetails({
        bookingId: res.data.booking_id,
        name: `Custom Trip to ${formData.destination}`,
        details: `${formData.travelers || 1} Traveler(s) • ${formData.budget || 'Budget flexible'}`,
        date: formData.travel_date || 'Flexible dates',
        travelers: parseInt(formData.travelers) || 1
      });
      setShowConfirmation(true);
      
      // Reset form
      setFormData({
        name: '',
        mobile: '',
        email: '',
        destination: '',
        travelers: '',
        travel_date: '',
        budget: '',
        requirements: '',
        inquiry_type: 'trip_planner'
      });
    } catch (err) {
      toast.error('Error submitting request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pt-20" data-testid="trip-planner-page">
      {/* Hero */}
      <section className="relative h-64 md:h-80 bg-gradient-to-r from-teal-600 to-teal-800 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=1920"
            alt="Trip Planning"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-medium text-white mb-4"
          >
            Custom Trip Planner
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-xl text-teal-100 max-w-2xl"
          >
            Tell us your dream destination and we'll create a personalized travel plan just for you
          </motion.p>
        </div>
      </section>

      {/* Form */}
      <section className="max-w-3xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-lg p-8"
        >
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="trip-form">
            {/* Personal Details */}
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Personal Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Doe"
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none"
                    data-testid="name-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Mobile Number *</label>
                  <input
                    type="tel"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    placeholder="+91 98765 43210"
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none"
                    data-testid="mobile-input"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Email Address</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none"
                    data-testid="email-input"
                  />
                </div>
              </div>
            </div>

            {/* Trip Details */}
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Trip Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">
                    <MapPin className="w-4 h-4 inline mr-1" />
                    Destination *
                  </label>
                  <DestinationAutocomplete
                    value={formData.destination}
                    onChange={(val) => setFormData({ ...formData, destination: val })}
                    placeholder="Maldives, Bali, etc."
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">
                    <Users className="w-4 h-4 inline mr-1" />
                    Number of Travelers
                  </label>
                  <select
                    name="travelers"
                    value={formData.travelers}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none bg-white"
                    data-testid="travelers-input"
                  >
                    <option value="">Select</option>
                    <option value="1">1 Person</option>
                    <option value="2">2 People (Couple)</option>
                    <option value="3">3-4 People</option>
                    <option value="5">5-8 People (Group)</option>
                    <option value="9">9+ People (Large Group)</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">
                    <Calendar className="w-4 h-4 inline mr-1" />
                    Travel Date
                  </label>
                  <input
                    type="date"
                    name="travel_date"
                    value={formData.travel_date}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none"
                    data-testid="date-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">
                    <Wallet className="w-4 h-4 inline mr-1" />
                    Budget Range
                  </label>
                  <select
                    name="budget"
                    value={formData.budget}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none bg-white"
                    data-testid="budget-input"
                  >
                    <option value="">Select</option>
                    <option value="budget">Budget (Under ₹25,000)</option>
                    <option value="standard">Standard (₹25,000 - ₹50,000)</option>
                    <option value="premium">Premium (₹50,000 - ₹1,00,000)</option>
                    <option value="luxury">Luxury (₹1,00,000+)</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Requirements */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">
                <FileText className="w-4 h-4 inline mr-1" />
                Special Requirements
              </label>
              <textarea
                name="requirements"
                value={formData.requirements}
                onChange={handleChange}
                placeholder="Any specific preferences, dietary requirements, activities you'd like to include..."
                rows={4}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none resize-none"
                data-testid="requirements-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-xl py-6 text-lg font-medium"
              data-testid="submit-trip-btn"
            >
              {loading ? (
                'Submitting...'
              ) : (
                <>
                  <Send className="w-5 h-5 mr-2 inline" />
                  Plan My Trip
                </>
              )}
            </Button>
          </form>
        </motion.div>
      </section>

      {/* Booking Confirmation Modal */}
      <AnimatePresence>
        {showConfirmation && (
          <BookingConfirmation
            bookingType="trip"
            bookingDetails={bookingDetails}
            onClose={() => setShowConfirmation(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
